import React, { useMemo } from 'react';
import { GeoJSONSourceOptions } from 'mapbox-gl';

import { color } from '@strava/ui-tokens/js';

import { LAYER_ID_TO_DRAW_ON } from '../constants';
import { useMap } from '../hooks/useMap';
import { Layer } from '../Layer/Layer';
import { Source } from '../Source/Source';
import { LatLng, LngLat } from '../types';
import { getBeforeIdToUse, latLngToLngLat } from '../utils';

export interface PolylineStyle {
  lineColor?: string;
  lineWidth?: number;
  outlineColor?: string;
  outlineWidth?: number;
  opacity?: number;
}

export interface PolylineProps {
  /**
   * Unique ID of the layer.
   */
  id: string;

  /**
   * Coordinates of polyline.
   */
  latLng: LatLng[];

  /**
   * Polyline style and how it looks.
   */
  style?: PolylineStyle;

  /**
   * The ID of an existing layer to insert the new layer before, resulting in the new
   * layer appearing visually beneath the existing layer.
   */
  beforeId?: string;
}

export const polylineDefaultStyle: PolylineStyle = {
  lineColor: color.colorExtendedRedR2,
  lineWidth: 3,
  outlineColor: color.colorCoreWhite,
  outlineWidth: 5,
  opacity: 1
};

export const Polyline = ({
  id,
  latLng,
  style: customStyle,
  beforeId = LAYER_ID_TO_DRAW_ON
}: PolylineProps) => {
  const map = useMap();

  const style = {
    ...polylineDefaultStyle,
    ...customStyle
  };

  const data = useMemo<GeoJSONSourceOptions['data']>(
    () => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: latLngToLngLat(latLng) as LngLat[]
          },
          properties: {
            id
          }
        }
      ]
    }),
    [latLng]
  );

  const beforeIdToUse = map ? getBeforeIdToUse(map, beforeId) : undefined;

  return (
    <Source
      id={`${id}-source`}
      type="geojson"
      data={data}
      layers={({ sourceId }) => (
        <>
          <Layer
            source={sourceId}
            id={`${id}-outline`}
            type="line"
            beforeId={beforeIdToUse}
            layout={{
              'line-cap': 'round',
              'line-join': 'round'
            }}
            paint={{
              'line-width': style.outlineWidth,
              'line-color': style.outlineColor,
              'line-color-transition': {
                duration: 0
              },
              'line-opacity': style.opacity
            }}
          />
          <Layer
            source={sourceId}
            id={id}
            type="line"
            beforeId={beforeIdToUse}
            layout={{
              'line-cap': 'round',
              'line-join': 'round'
            }}
            paint={{
              'line-width': style.lineWidth,
              'line-color': style.lineColor,
              'line-color-transition': {
                duration: 0
              },
              'line-opacity': style.opacity
            }}
          />
        </>
      )}
    />
  );
};
